import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Trash2, Check, FileText, Download, Copy, Star, Globe,
} from 'lucide-react';
import { API } from './constants';

const STATIC_ASSET = {
  id: 'wg-gold-logo',
  name: 'White Glove Gold Logo',
  description: 'Primary gold brand mark. Used as the site favicon. Reserved for admin / internal use.',
  path: '/assets/wg-logo.jpg',
  content_type: 'image/jpeg',
  isStatic: true,
};

const isImageType = (ct) => ct && ct.startsWith('image/');
const isVideoType = (ct) => ct && ct.startsWith('video/');

const AssetUploadForm = ({ onUploaded }) => {
  const [file, setFile] = useState(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleFile = (f) => {
    if (!f) return;
    if (f.size > 100 * 1024 * 1024) {
      toast.error('File exceeds 100 MB limit');
      return;
    }
    setFile(f);
    if (!name) {
      const baseName = f.name.replace(/\.[^/.]+$/, '');
      setName(baseName);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file || !name.trim()) {
      toast.error('Name and file are required');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('name', name.trim());
      formData.append('description', description.trim());
      await axios.post(`${API}/admin/brand-assets`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Asset uploaded');
      setFile(null);
      setName('');
      setDescription('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      onUploaded();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Upload failed';
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="mb-10 p-6 bg-[#0a0a0a] border border-white/5"
      data-testid="asset-upload-form"
    >
      <h3 className="font-heading text-white text-lg mb-4">Upload New Asset</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-white/60 text-xs uppercase tracking-widest mb-2">Name *</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Primary Logotype"
            className="w-full px-4 py-3 bg-[#050505] border border-white/10 text-white text-sm focus:outline-none focus:border-[#D4AF37]"
            data-testid="asset-name-input"
            required
          />
        </div>
        <div>
          <label className="block text-white/60 text-xs uppercase tracking-widest mb-2">File *</label>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml,application/pdf,video/mp4,video/webm,video/quicktime"
            onChange={(e) => handleFile(e.target.files?.[0])}
            className="w-full px-4 py-3 bg-[#050505] border border-white/10 text-white text-sm file:mr-3 file:py-1.5 file:px-3 file:border-0 file:bg-[#D4AF37] file:text-black file:text-xs file:uppercase file:tracking-widest file:cursor-pointer"
            data-testid="asset-file-input"
            required
          />
        </div>
      </div>
      <div className="mb-4">
        <label className="block text-white/60 text-xs uppercase tracking-widest mb-2">Description</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="What this asset is used for..."
          rows={2}
          className="w-full px-4 py-3 bg-[#050505] border border-white/10 text-white text-sm focus:outline-none focus:border-[#D4AF37] resize-none"
          data-testid="asset-description-input"
        />
      </div>
      <div className="flex items-center justify-between">
        <p className="text-white/40 text-xs">
          Accepted: JPG, PNG, GIF, WEBP, SVG, PDF, MP4, WEBM, MOV · Max 100 MB
        </p>
        <button
          type="submit"
          disabled={uploading || !file}
          className="flex items-center gap-2 px-6 py-2.5 bg-[#D4AF37] text-black text-xs uppercase tracking-widest hover:bg-[#C6A86A] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          data-testid="asset-upload-submit"
        >
          {uploading ? 'Uploading…' : 'Upload Asset'}
        </button>
      </div>
    </form>
  );
};

const UploadedAssetPreview = ({ asset }) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const isImg = isImageType(asset.content_type);
  const isVid = isVideoType(asset.content_type);

  useEffect(() => {
    let revoked = null;
    if (isImg || isVid) {
      axios
        .get(`${API}/admin/brand-assets/${asset.id}/download`, {
          withCredentials: true,
          responseType: 'blob',
        })
        .then((res) => {
          const url = URL.createObjectURL(res.data);
          revoked = url;
          setBlobUrl(url);
        })
        .catch(() => {});
    }
    return () => {
      if (revoked) URL.revokeObjectURL(revoked);
    };
  }, [asset.id, isImg, isVid]);

  if (isImg) {
    return blobUrl ? (
      <img
        src={blobUrl}
        alt={asset.name}
        className="max-w-full max-h-full object-contain"
        data-testid={`asset-preview-${asset.id}`}
      />
    ) : (
      <div className="w-10 h-10 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
    );
  }

  if (isVid) {
    return blobUrl ? (
      <video
        src={blobUrl}
        controls
        muted
        playsInline
        preload="metadata"
        className="max-w-full max-h-full object-contain"
        data-testid={`asset-preview-${asset.id}`}
      />
    ) : (
      <div className="w-10 h-10 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
    );
  }

  return (
    <div className="text-center" data-testid={`asset-preview-${asset.id}`}>
      <FileText className="w-14 h-14 text-[#D4AF37] mx-auto mb-2" />
      <p className="text-white/50 text-xs uppercase tracking-widest">
        {asset.content_type?.split('/')[1] || 'file'}
      </p>
    </div>
  );
};

export const AssetsTab = () => {
  const [uploaded, setUploaded] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState(null);
  const [navbarLogoAssetId, setNavbarLogoAssetId] = useState(null);
  const [faviconAssetId, setFaviconAssetId] = useState(null);

  const fetchAssets = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/brand-assets`, { withCredentials: true });
      setUploaded(res.data);
    } catch (err) {
      console.error('Failed to load assets', err);
      toast.error('Failed to load assets');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Single mount-time bootstrap — fires the asset listing alongside the
    // two site-setting lookups in parallel.
    let cancelled = false;
    (async () => {
      const [assetsRes, logoRes, faviconRes] = await Promise.allSettled([
        axios.get(`${API}/admin/brand-assets`, { withCredentials: true }),
        axios.get(`${API}/site-settings/navbar-logo`),
        axios.get(`${API}/site-settings/favicon`),
      ]);
      if (cancelled) return;
      if (assetsRes.status === 'fulfilled') {
        setUploaded(assetsRes.value.data);
      } else {
        toast.error('Failed to load assets');
      }
      setNavbarLogoAssetId(
        logoRes.status === 'fulfilled' ? logoRes.value.data?.asset_id || null : null,
      );
      setFaviconAssetId(
        faviconRes.status === 'fulfilled' ? faviconRes.value.data?.asset_id || null : null,
      );
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  const copyPath = (id, url) => {
    navigator.clipboard.writeText(url);
    setCopiedId(id);
    toast.success('Asset URL copied to clipboard');
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleDownloadUploaded = async (asset) => {
    try {
      const res = await axios.get(`${API}/admin/brand-assets/${asset.id}/download`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = asset.original_filename || asset.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Download failed');
    }
  };

  const handleDelete = async (asset) => {
    if (!window.confirm(`Delete "${asset.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/brand-assets/${asset.id}`, { withCredentials: true });
      toast.success('Asset deleted');
      fetchAssets();
      if (navbarLogoAssetId === asset.id) {
        setNavbarLogoAssetId(null);
      }
      if (faviconAssetId === asset.id) {
        setFaviconAssetId(null);
      }
    } catch {
      toast.error('Delete failed');
    }
  };

  const handleSetNavbarLogo = async (asset) => {
    const isActive = navbarLogoAssetId === asset.id;
    const nextAssetId = isActive ? null : asset.id;
    try {
      await axios.post(
        `${API}/admin/site-settings/navbar-logo`,
        { asset_id: nextAssetId },
        { withCredentials: true },
      );
      setNavbarLogoAssetId(nextAssetId);
      toast.success(isActive ? 'Reverted to default navbar logo' : 'Navbar logo updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update navbar logo');
    }
  };

  const handleSetFavicon = async (asset) => {
    const isActive = faviconAssetId === asset.id;
    const nextAssetId = isActive ? null : asset.id;
    try {
      await axios.post(
        `${API}/admin/site-settings/favicon`,
        { asset_id: nextAssetId },
        { withCredentials: true },
      );
      setFaviconAssetId(nextAssetId);
      toast.success(
        isActive
          ? 'Reverted to default favicon'
          : 'Favicon updated — browser may take up to an hour to refresh due to caching'
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update favicon');
    }
  };

  return (
    <div data-testid="admin-assets-tab">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="font-heading text-2xl text-white mb-2">Brand Assets</h2>
          <p className="text-white/50 text-sm">
            Private library of White Glove brand assets. Admin access only.
          </p>
        </div>
        <span className="text-[10px] uppercase tracking-widest text-[#D4AF37] border border-[#D4AF37]/40 px-3 py-1.5">
          Admin Only
        </span>
      </div>

      <AssetUploadForm onUploaded={fetchAssets} />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Built-in favicon asset */}
        <div
          className="bg-[#0a0a0a] border border-white/5 hover:border-[#D4AF37]/30 transition-colors"
          data-testid={`asset-card-${STATIC_ASSET.id}`}
        >
          <div className="aspect-square bg-black flex items-center justify-center p-8 border-b border-white/5">
            <img
              src={STATIC_ASSET.path}
              alt={STATIC_ASSET.name}
              className="max-w-full max-h-full object-contain"
              data-testid={`asset-preview-${STATIC_ASSET.id}`}
            />
          </div>
          <div className="p-5">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-heading text-white text-lg">{STATIC_ASSET.name}</h3>
              <span className="text-[10px] uppercase tracking-widest text-[#D4AF37]">
                Favicon
              </span>
            </div>
            <p className="text-white/50 text-xs leading-relaxed mb-4">
              {STATIC_ASSET.description}
            </p>
            <div className="flex items-center gap-2">
              <a
                href={STATIC_ASSET.path}
                download
                className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 bg-[#D4AF37] text-black text-xs uppercase tracking-widest hover:bg-[#C6A86A] transition-colors"
                data-testid={`asset-download-${STATIC_ASSET.id}`}
              >
                <Download className="w-3.5 h-3.5" />
                Download
              </a>
              <button
                onClick={() => copyPath(STATIC_ASSET.id, `${window.location.origin}${STATIC_ASSET.path}`)}
                className="flex items-center justify-center gap-2 px-3 py-2.5 bg-[#1B1B1B] text-white text-xs uppercase tracking-widest hover:bg-[#111111] transition-colors border border-white/10"
                data-testid={`asset-copy-${STATIC_ASSET.id}`}
              >
                {copiedId === STATIC_ASSET.id ? (
                  <Check className="w-3.5 h-3.5 text-[#D4AF37]" />
                ) : (
                  <Copy className="w-3.5 h-3.5" />
                )}
                {copiedId === STATIC_ASSET.id ? 'Copied' : 'Copy URL'}
              </button>
            </div>
          </div>
        </div>

        {/* Uploaded assets */}
        {uploaded.map((asset) => {
          const isActiveLogo = navbarLogoAssetId === asset.id;
          const isActiveFavicon = faviconAssetId === asset.id;
          const canBeLogo = isImageType(asset.content_type);
          const canBeFavicon = isImageType(asset.content_type);
          return (
          <div
            key={asset.id}
            className={`bg-[#0a0a0a] border ${isActiveLogo || isActiveFavicon ? 'border-[#D4AF37]' : 'border-white/5 hover:border-[#D4AF37]/30'} transition-colors group`}
            data-testid={`asset-card-${asset.id}`}
          >
            <div className="aspect-square bg-black flex items-center justify-center p-8 border-b border-white/5 relative">
              <UploadedAssetPreview asset={asset} />
              <div className="absolute top-3 left-3 flex flex-col gap-1.5">
                {isActiveLogo && (
                  <span
                    className="px-2 py-1 bg-[#D4AF37] text-black text-[9px] uppercase tracking-widest font-medium flex items-center gap-1.5"
                    data-testid={`asset-active-logo-${asset.id}`}
                  >
                    <Star className="w-3 h-3 fill-black" />
                    Navbar Logo
                  </span>
                )}
                {isActiveFavicon && (
                  <span
                    className="px-2 py-1 bg-[#D4AF37] text-black text-[9px] uppercase tracking-widest font-medium flex items-center gap-1.5"
                    data-testid={`asset-active-favicon-${asset.id}`}
                  >
                    <Globe className="w-3 h-3" />
                    Favicon
                  </span>
                )}
              </div>
              <button
                onClick={() => handleDelete(asset)}
                className="absolute top-3 right-3 p-2 bg-black/70 hover:bg-red-500/20 text-white/60 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all border border-white/10"
                data-testid={`asset-delete-${asset.id}`}
                title="Delete asset"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="p-5">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-heading text-white text-lg truncate">{asset.name}</h3>
                <span className="text-[10px] uppercase tracking-widest text-white/40 flex-shrink-0 ml-2">
                  {(asset.content_type?.split('/')[1] || 'file').toUpperCase()}
                </span>
              </div>
              {asset.description && (
                <p className="text-white/50 text-xs leading-relaxed mb-4 line-clamp-2">
                  {asset.description}
                </p>
              )}
              {canBeLogo && (
                <button
                  onClick={() => handleSetNavbarLogo(asset)}
                  className={`w-full flex items-center justify-center gap-2 px-3 py-2 mb-2 text-xs uppercase tracking-widest transition-colors border ${
                    isActiveLogo
                      ? 'bg-[#D4AF37]/10 text-[#D4AF37] border-[#D4AF37]/40 hover:bg-[#D4AF37]/20'
                      : 'bg-transparent text-white/70 border-white/10 hover:text-[#D4AF37] hover:border-[#D4AF37]/40'
                  }`}
                  data-testid={`asset-set-navbar-logo-${asset.id}`}
                  title={isActiveLogo ? 'Click to revert to default logo' : 'Use this asset as the site navbar logo'}
                >
                  <Star className={`w-3.5 h-3.5 ${isActiveLogo ? 'fill-[#D4AF37]' : ''}`} />
                  {isActiveLogo ? 'Active Navbar Logo · Click to revert' : 'Set as Navbar Logo'}
                </button>
              )}
              {canBeFavicon && (
                <button
                  onClick={() => handleSetFavicon(asset)}
                  className={`w-full flex items-center justify-center gap-2 px-3 py-2 mb-2 text-xs uppercase tracking-widest transition-colors border ${
                    isActiveFavicon
                      ? 'bg-[#D4AF37]/10 text-[#D4AF37] border-[#D4AF37]/40 hover:bg-[#D4AF37]/20'
                      : 'bg-transparent text-white/70 border-white/10 hover:text-[#D4AF37] hover:border-[#D4AF37]/40'
                  }`}
                  data-testid={`asset-set-favicon-${asset.id}`}
                  title={isActiveFavicon ? 'Click to revert to default favicon' : 'Use this asset to generate the browser favicon & iOS touch icon'}
                >
                  <Globe className={`w-3.5 h-3.5`} />
                  {isActiveFavicon ? 'Active Favicon · Click to revert' : 'Set as Favicon'}
                </button>
              )}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleDownloadUploaded(asset)}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 bg-[#D4AF37] text-black text-xs uppercase tracking-widest hover:bg-[#C6A86A] transition-colors"
                  data-testid={`asset-download-${asset.id}`}
                >
                  <Download className="w-3.5 h-3.5" />
                  Download
                </button>
                <button
                  onClick={() => copyPath(asset.id, `${API}/admin/brand-assets/${asset.id}/download`)}
                  className="flex items-center justify-center gap-2 px-3 py-2.5 bg-[#1B1B1B] text-white text-xs uppercase tracking-widest hover:bg-[#111111] transition-colors border border-white/10"
                  data-testid={`asset-copy-${asset.id}`}
                >
                  {copiedId === asset.id ? (
                    <Check className="w-3.5 h-3.5 text-[#D4AF37]" />
                  ) : (
                    <Copy className="w-3.5 h-3.5" />
                  )}
                  {copiedId === asset.id ? 'Copied' : 'Copy URL'}
                </button>
              </div>
            </div>
          </div>
          );
        })}

        {!loading && uploaded.length === 0 && (
          <div className="md:col-span-2 p-6 text-center text-white/40 text-sm border border-dashed border-white/10">
            No uploaded assets yet. Use the form above to add your first one.
          </div>
        )}
      </div>

      <div className="mt-8 p-5 bg-[#0a0a0a] border border-white/5">
        <p className="text-white/40 text-xs uppercase tracking-widest mb-2">Usage Notice</p>
        <p className="text-white/60 text-sm leading-relaxed">
          These assets are proprietary to White Glove Event Concierge and are restricted to internal
          administrative use. Do not distribute outside authorized channels without written consent.
        </p>
      </div>
    </div>
  );
};
